"use client";

import { useCallback } from "react";

import { useToast } from "@/components/providers/FetchToastProvider";

import { useEstimator } from "../_context/EstimatorContext";
import type { WorkspaceAction } from "../_logic/types";

/**
 * A planner's inline edit (a count, a fare, an airport) through the same reducer as the chat,
 * confirmed with a toast that can undo it.
 * @returns `apply(action, message)`, which edits and shows the toast, or the error when rejected.
 * @throws Never.
 */
export function useUndoableEdit(): (action: WorkspaceAction, message: string) => void {
  // Hooks
  const { edit } = useEstimator();
  const { showToast } = useToast();

  // Handlers
  return useCallback(
    (action: WorkspaceAction, message: string) => {
      const result = edit(action, "user");
      if (!result.ok) {
        showToast(result.error, "error");
        return;
      }
      showToast(message, "success", {
        action: { label: "Undo", onClick: () => edit(result.undo, "user") },
      });
    },
    [edit, showToast]
  );
}
